import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Clipboard } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';

/**
 * Auto-growing textarea that always shows the full text of its value.
 * Recomputes height on every change so the user never sees a cropped cell.
 */
function AutoTextarea({ value, onChange, onPaste, placeholder, className, ...rest }) {
  const ref = useRef(null);

  const rafRef = useRef(null);

  // Batched via requestAnimationFrame so many cells resizing at once (e.g. after pasting a table)
  // never trigger the browser's "ResizeObserver loop" warning.
  const resize = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = ref.current;
      if (!el) return;
      const next = `${Math.max(el.scrollHeight, 0)}px`;
      if (el.style.height === next) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    });
  };

  useEffect(() => { resize(); }, [value]);
  useEffect(() => {
    // Resize on mount + on window resize (column width may change)
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <textarea
      ref={ref}
      value={value || ''}
      onChange={(e) => { onChange?.(e); resize(); }}
      onPaste={onPaste}
      placeholder={placeholder}
      rows={1}
      className={className}
      style={{ overflow: 'hidden', resize: 'none' }}
      {...rest}
    />
  );
}

// 7 fixed columns. Keep IDs short — they are stored on backend.
export const GUION_COLUMNS = [
  { id: 'tiempo',          label: 'Tiempo',           width: '90px',  placeholder: '0-3s' },
  { id: 'tipo_voz',        label: 'Tipo de voz',      width: '110px', placeholder: 'Off / En cámara' },
  { id: 'plano',           label: 'Plano sugerido',   width: '140px', placeholder: 'Frontal medio' },
  { id: 'guion',           label: 'Guion',            width: '260px', placeholder: 'Texto que se dice' },
  { id: 'broll',           label: 'B-roll',           width: '180px', placeholder: 'Imágenes / clips' },
  { id: 'texto_pantalla',  label: 'Texto en pantalla', width: '180px', placeholder: '"¿Sabías que..."' },
  { id: 'notas_edicion',   label: 'Notas edición',    width: '180px', placeholder: 'Subtítulos amarillos' },
];

const COLUMN_IDS = GUION_COLUMNS.map((c) => c.id);

const emptyRow = () => COLUMN_IDS.reduce((acc, id) => { acc[id] = ''; return acc; }, {});

/**
 * Normalize a header cell label to one of our 7 column IDs.
 * Accepts both exact labels and common variants (case-insensitive, accent-insensitive).
 */
function normalizeHeaderToColumnId(rawHeader) {
  const norm = String(rawHeader || '')
    .replace(/\*\*|__|`/g, '') // negritas/código de Markdown
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const map = {
    'tiempo': 'tiempo', 'time': 'tiempo', 'momento': 'tiempo', 't': 'tiempo',
    'tipo de voz': 'tipo_voz', 'voz': 'tipo_voz', 'voiceover': 'tipo_voz', 'voice over': 'tipo_voz', 'locucion': 'tipo_voz',
    'plano sugerido': 'plano', 'plano': 'plano', 'shot': 'plano', 'camara': 'plano', 'encuadre': 'plano',
    'guion': 'guion', 'libreto': 'guion', 'script': 'guion', 'dialogo': 'guion', 'texto': 'guion',
    'broll': 'broll', 'b roll': 'broll', 'imagenes': 'broll', 'clips': 'broll', 'recursos': 'broll',
    'texto en pantalla': 'texto_pantalla', 'overlay': 'texto_pantalla', 'subtitulo': 'texto_pantalla', 'on screen': 'texto_pantalla', 'tip': 'texto_pantalla',
    'notas edicion': 'notas_edicion', 'notas de edicion': 'notas_edicion', 'notas': 'notas_edicion', 'edicion': 'notas_edicion', 'editor': 'notas_edicion', 'observaciones': 'notas_edicion',
  };
  return map[norm] || null;
}

/**
 * Parse a Markdown table (pipe syntax) into an array of row objects keyed by our column IDs.
 * Returns null if input is not a recognizable table.
 */
export function parseMarkdownTable(text) {
  if (!text) return null;
  // Strip optional language fence
  let body = text.trim();
  if (body.startsWith('```')) {
    body = body.replace(/^```\w*\n?/, '').replace(/```\s*$/, '').trim();
  }
  const allLines = body.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  // Find first line that looks like a table row
  const tableLines = allLines.filter((l) => l.includes('|'));
  if (tableLines.length < 2) return null;

  // Helper to split pipe-row
  const ESC = '\u0000';
  const clean = (c) => c.replace(new RegExp(ESC, 'g'), '|').replace(/<br\s*\/?>/gi, '\n').replace(/\u00a0/g, ' ').trim();
  const splitRow = (l) => {
    let s = l.trim().replace(/\\\|/g, ESC); // "\|" dentro de una celda no es separador
    if (s.startsWith('|')) s = s.slice(1);
    if (s.endsWith('|')) s = s.slice(0, -1);
    return s.split('|').map(clean);
  };

  const headerCells = splitRow(tableLines[0]);
  // Validate it has at least 2 cells (y al menos 2 con texto: "|||" no es una tabla)
  if (headerCells.length < 2 || headerCells.filter(Boolean).length < 2) return null;

  // Detect separator row (e.g. |---|---|), skip it if present
  let dataStartIdx = 1;
  const sep = splitRow(tableLines[1] || '');
  const isSeparator = sep.length > 0 && sep.every((c) => /^:?-+:?$/.test(c.replace(/\s+/g, '')));
  if (isSeparator) dataStartIdx = 2;

  // Map header → column id
  const headerToColId = headerCells.map(normalizeHeaderToColumnId);
  // If we couldn't map any header, treat the table as a positional fallback against our 7 columns in order
  const hasAnyMapping = headerToColId.some(Boolean);
  if (hasAnyMapping) {
    // Cabeceras no reconocidas → se asignan por posición a las columnas que queden libres
    const used = new Set(headerToColId.filter(Boolean));
    headerToColId.forEach((id, idx) => {
      if (id) return;
      const free = COLUMN_IDS[idx] && !used.has(COLUMN_IDS[idx]) ? COLUMN_IDS[idx] : COLUMN_IDS.find((c) => !used.has(c));
      if (free) { headerToColId[idx] = free; used.add(free); }
    });
  }

  const rows = [];
  for (let i = dataStartIdx; i < tableLines.length; i++) {
    const cells = splitRow(tableLines[i]);
    // Skip empty rows
    if (cells.every((c) => !c)) continue;
    const row = emptyRow();
    if (hasAnyMapping) {
      cells.forEach((value, idx) => {
        const colId = headerToColId[idx];
        if (colId && (row[colId] === '' || row[colId] === undefined)) {
          row[colId] = value;
        }
      });
    } else {
      cells.slice(0, COLUMN_IDS.length).forEach((value, idx) => {
        row[COLUMN_IDS[idx]] = value;
      });
    }
    rows.push(row);
  }
  return rows.length > 0 ? rows : null;
}

/**
 * Parse a tab-separated table (what you usually get when you select+copy a rendered table
 * from Claude/ChatGPT/Notion/Google Docs). Falls back to >=2 spaces as a column separator
 * if no tabs are detected but lines are clearly aligned.
 */
export function parseTSVTable(text) {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, '\n').split('\n').map((l) => l.trimEnd()).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return null;

  // Decide delimiter: prefer tab, then 2+ spaces
  const hasTabs = lines.some((l) => l.includes('\t'));
  const split = (l) => hasTabs ? l.split('\t').map((c) => c.trim()) : l.split(/\s{2,}/).map((c) => c.trim());

  const headerCells = split(lines[0]).filter((c) => c.length > 0);
  if (headerCells.length < 2) return null;
  const headerToColId = headerCells.map(normalizeHeaderToColumnId);
  const hasAnyMapping = headerToColId.some(Boolean);
  if (!hasAnyMapping && headerCells.length !== COLUMN_IDS.length) return null;

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = split(lines[i]);
    if (cells.every((c) => !c)) continue;
    // Skip markdown-like separator rows that may sneak in
    if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue;
    const row = emptyRow();
    if (hasAnyMapping) {
      cells.forEach((value, idx) => {
        const colId = headerToColId[idx];
        if (colId) row[colId] = value;
      });
    } else {
      cells.slice(0, COLUMN_IDS.length).forEach((v, idx) => { row[COLUMN_IDS[idx]] = v; });
    }
    rows.push(row);
  }
  return rows.length > 0 ? rows : null;
}

/**
 * Auto-detect and parse: tries Markdown first, then TSV. Returns rows or null.
 */
export function parseAnyTable(text) {
  try {
    if (!text || typeof text !== 'string') return null;
    const trimmed = text.trim();
    // If it contains pipe-delimited markdown, prefer that
    if (trimmed.includes('|')) {
      const md = parseMarkdownTable(trimmed);
      if (md && md.length > 0) return md;
    }
    // Fallback to tab/space separated
    return parseTSVTable(trimmed);
  } catch (err) {
    // Nunca romper la app por un pegado raro: se trata como "no reconocido"
    console.warn('parseAnyTable: contenido no procesable', err);
    return null;
  }
}

/**
 * Serialize rows back to a Markdown table.
 */
export function rowsToMarkdownTable(rows) {
  if (!rows || rows.length === 0) return '';
  const headers = GUION_COLUMNS.map((c) => c.label);
  const lines = [];
  lines.push(`| ${headers.join(' | ')} |`);
  lines.push(`| ${headers.map(() => '---').join(' | ')} |`);
  for (const r of rows) {
    const cells = COLUMN_IDS.map((id) => String(r[id] ?? '').replace(/\|/g, '\\|').replace(/\n/g, ' '));
    lines.push(`| ${cells.join(' | ')} |`);
  }
  return lines.join('\n');
}

/**
 * Try to convert the legacy free-text `guion` field (often a markdown table) to structured rows.
 * Returns null if not parseable.
 */
export function legacyGuionToRows(text) {
  if (!text || typeof text !== 'string') return null;
  return parseMarkdownTable(text);
}

export default function GuionTableEditor({ rows = [], onChange, readOnly = false }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteMode, setPasteMode] = useState('append'); // 'append' | 'replace'
  const [pastePreview, setPastePreview] = useState(null);
  const [pasteError, setPasteError] = useState('');
  const [pasteInsertAt, setPasteInsertAt] = useState(null); // fila donde se pegó directamente (null = desde el botón)

  const safeRows = Array.isArray(rows) ? rows : [];

  const setCell = (rowIdx, colId, value) => {
    const next = safeRows.map((r, i) => (i === rowIdx ? { ...r, [colId]: value } : r));
    onChange?.(next);
  };

  const addRow = () => {
    onChange?.([...safeRows, emptyRow()]);
  };

  const removeRow = (idx) => {
    onChange?.(safeRows.filter((_, i) => i !== idx));
  };

  // Pegar una tabla (Markdown/TSV) directamente sobre cualquier celda → abre la vista previa
  // con la tabla ya detectada; NUNCA se aplica sin confirmar. Un pegado normal (texto suelto) sigue igual.
  const handlePaste = (e, rowIdx, colId) => {
    const text = e.clipboardData?.getData('text');
    if (!text) return;
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return;
    if (!text.includes('|') && !text.includes('\t')) return;
    const parsed = parseAnyTable(text);
    if (!parsed || parsed.length === 0) return;
    e.preventDefault();
    setPasteText(text);
    setPastePreview(parsed);
    setPasteError('');
    setPasteInsertAt(rowIdx);
    setPasteMode('insert');
    setPasteOpen(true);
  };

  // Open the paste modal
  const openPasteModal = () => {
    setPasteText('');
    setPasteMode(safeRows.length > 0 ? 'append' : 'replace');
    setPastePreview(null);
    setPasteError('');
    setPasteInsertAt(null);
    setPasteOpen(true);
  };

  const isRowEmpty = (r) => COLUMN_IDS.every((id) => !String(r?.[id] || '').trim());

  // Re-parse whenever pasteText changes (live preview)
  const handlePasteTextChange = (val) => {
    setPasteText(val);
    setPasteError('');
    if (!val.trim()) {
      setPastePreview(null);
      return;
    }
    const parsed = parseAnyTable(val);
    if (parsed && parsed.length > 0) {
      setPastePreview(parsed);
    } else {
      setPastePreview(null);
      setPasteError('No se reconoce una tabla en el contenido pegado. Comprueba que es una tabla Markdown (con |) o tabulada (con tabs).');
    }
  };

  const confirmPaste = () => {
    if (!pastePreview || pastePreview.length === 0) {
      setPasteError('No hay nada que importar.');
      return;
    }
    let next;
    if (pasteMode === 'replace') next = [...pastePreview];
    else if (pasteMode === 'insert' && pasteInsertAt !== null) {
      // Si la fila donde se pegó está vacía, se sustituye; si tiene contenido, se inserta debajo
      const at = Math.min(pasteInsertAt, safeRows.length);
      const replaceTarget = safeRows[at] && isRowEmpty(safeRows[at]);
      next = [...safeRows.slice(0, at), ...pastePreview, ...safeRows.slice(replaceTarget ? at + 1 : at + (safeRows[at] ? 1 : 0))];
    } else next = [...safeRows, ...pastePreview];
    onChange?.(next);
    setPasteOpen(false);
    setPasteText('');
    setPastePreview(null);
    setPasteInsertAt(null);
  };

  return (
    <div className="space-y-2" data-testid="guion-table-editor">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {safeRows.length === 0 ? 'Tabla vacía. Añade filas o pega una tabla en Markdown.' : `${safeRows.length} fila${safeRows.length === 1 ? '' : 's'}`}
        </p>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={openPasteModal} data-testid="guion-paste-markdown-btn">
              <Clipboard className="w-3.5 h-3.5 mr-1.5" /> Pegar tabla
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="guion-add-row-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Añadir fila
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto border rounded-md">
        <table className="text-xs border-collapse" style={{ minWidth: '100%' }}>
          <thead className="bg-muted/60 sticky top-0">
            <tr>
              {GUION_COLUMNS.map((c) => (
                <th
                  key={c.id}
                  className="px-2 py-1.5 text-left font-semibold text-muted-foreground border-b border-r last:border-r-0"
                  style={{ minWidth: c.width, width: c.width }}
                >
                  {c.label}
                </th>
              ))}
              {!readOnly && <th className="border-b w-9"></th>}
            </tr>
          </thead>
          <tbody>
            {safeRows.length === 0 ? (
              <tr>
                <td colSpan={GUION_COLUMNS.length + (readOnly ? 0 : 1)} className="text-center py-6 text-muted-foreground italic">
                  Sin contenido. Pulsa &quot;Añadir fila&quot; o &quot;Pegar tabla Markdown&quot;.
                </td>
              </tr>
            ) : (
              safeRows.map((row, idx) => (
                <tr key={idx} className="border-b last:border-b-0 hover:bg-muted/20" data-testid={`guion-row-${idx}`}>
                  {GUION_COLUMNS.map((c) => (
                    <td key={c.id} className="border-r last:border-r-0 align-top p-0">
                      {readOnly ? (
                        <div className="px-2 py-1.5 whitespace-pre-wrap break-words min-h-[28px]">{row[c.id] || ''}</div>
                      ) : (
                        <AutoTextarea
                          value={row[c.id] || ''}
                          onChange={(e) => setCell(idx, c.id, e.target.value)}
                          onPaste={(e) => handlePaste(e, idx, c.id)}
                          placeholder={c.placeholder}
                          className="w-full px-2 py-1.5 bg-transparent outline-none focus:bg-primary/5 focus:ring-1 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground/50 whitespace-pre-wrap break-words leading-snug"
                          data-testid={`guion-cell-${idx}-${c.id}`}
                        />
                      )}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        title="Eliminar fila"
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`guion-remove-row-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paste Markdown / TSV table dialog */}
      <Dialog open={pasteOpen} onOpenChange={(o) => { setPasteOpen(o); if (!o) setPasteInsertAt(null); }}>
        <DialogContent className="max-w-3xl" data-testid="guion-paste-dialog">
          <DialogHeader>
            <DialogTitle>Pegar tabla del guion</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Pega aquí la tabla copiada de Claude / ChatGPT / Notion / Google Docs.
              Detectamos automáticamente formato Markdown (con <code className="px-1 rounded bg-muted">|</code>) o tabulado (con tabs).
              Las columnas se mapean por nombre — tolerante a acentos y mayúsculas.
            </p>

            <Textarea
              autoFocus
              rows={10}
              placeholder={'| Tiempo | Tipo de voz | Plano sugerido | Guion | B-roll | Texto en pantalla | Notas edición |\n|---|---|---|---|---|---|---|\n| 0-3s | Off | Frontal medio | Hook potente | Logo | 3 claves | Subtítulos amarillos |\n...'}
              value={pasteText}
              onChange={(e) => handlePasteTextChange(e.target.value)}
              className="font-mono text-xs"
              data-testid="guion-paste-textarea"
            />

            {pasteError && (
              <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded p-2" data-testid="guion-paste-error">
                {pasteError}
              </div>
            )}

            {pastePreview && pastePreview.length > 0 && (
              <div className="space-y-2" data-testid="guion-paste-preview">
                <p className="text-xs font-medium text-foreground">
                  ✓ Detectadas {pastePreview.length} fila{pastePreview.length === 1 ? '' : 's'}
                </p>
                <div className="border rounded-md overflow-x-auto max-h-48">
                  <table className="text-[11px] w-full">
                    <thead className="bg-muted/60 sticky top-0">
                      <tr>
                        {GUION_COLUMNS.map((c) => (
                          <th key={c.id} className="px-2 py-1 text-left font-medium border-b border-r last:border-r-0">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pastePreview.slice(0, 6).map((r, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          {GUION_COLUMNS.map((c) => (
                            <td key={c.id} className="px-2 py-1 border-r last:border-r-0 align-top max-w-[180px] truncate" title={r[c.id]}>{r[c.id] || ''}</td>
                          ))}
                        </tr>
                      ))}
                      {pastePreview.length > 6 && (
                        <tr><td colSpan={GUION_COLUMNS.length} className="text-center py-1 text-muted-foreground">+ {pastePreview.length - 6} fila{pastePreview.length - 6 === 1 ? '' : 's'} más…</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {safeRows.length > 0 && (
                  <div className="flex flex-wrap items-center gap-4 text-xs pt-1">
                    {pasteInsertAt !== null && (
                      <label className="flex items-center gap-1.5 cursor-pointer" data-testid="guion-paste-mode-insert">
                        <input
                          type="radio"
                          name="paste-mode"
                          checked={pasteMode === 'insert'}
                          onChange={() => setPasteMode('insert')}
                        />
                        {isRowEmpty(safeRows[pasteInsertAt]) ? `En la fila ${pasteInsertAt + 1} (vacía)` : `Debajo de la fila ${pasteInsertAt + 1}`}
                      </label>
                    )}
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid="guion-paste-mode-append">
                      <input
                        type="radio"
                        name="paste-mode"
                        checked={pasteMode === 'append'}
                        onChange={() => setPasteMode('append')}
                      />
                      Añadir al final ({safeRows.length} + {pastePreview.length} = {safeRows.length + pastePreview.length})
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid="guion-paste-mode-replace">
                      <input
                        type="radio"
                        name="paste-mode"
                        checked={pasteMode === 'replace'}
                        onChange={() => setPasteMode('replace')}
                      />
                      Reemplazar las {safeRows.length} filas actuales
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPasteOpen(false)}>Cancelar</Button>
            <Button onClick={confirmPaste} disabled={!pastePreview || pastePreview.length === 0} data-testid="guion-paste-confirm-btn">
              <Clipboard className="w-3.5 h-3.5 mr-1.5" />
              {pasteMode === 'replace' ? 'Reemplazar tabla' : 'Importar filas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
